
import { useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { Badge, Link, Loader, TablePagination } from "@launchmetrics/lm-ui";
import { ExternalLinkOutline } from '@launchmetrics/lm-icons';
import GoTopButton from 'components/LM/common/GoTopButton';
import ProfileImage from "components/LM/common/ProfileImage";
import NumeralMetric from "components/LM/common/NumeralMetric";
import { profileTag } from "components/LM/SocialMediaProfile/SocialMediaProfile";
import VoiceRelationsIcons from "components/LM/common/VoiceRelationsIcons";
import CrawlingSwitch from "components/LM/SocialMediaProfile/CrawlingSwitch";
import AdvancedTable from 'components/LM/common/AdvancedTable';
import ExportCSV from "components/LM/common/ExportCSV";
import K2Api from 'lib/ajax/K2Api';

import './orphanmsearch.scss';

const TABLE_COLUMNS = [
	{
		name: "account",
		alias: "Account"
	},
	{
		name: "account_type",
		alias: "Account Type"
	},
	{
		name: "name_followers",
		alias: "Name & Followers"
	},
	{
		name: "crawling",
		alias: "Crawling"
	},
	{
		name: "customers",
		alias: "Customers"
	}
	,
	{
		name: "voice_id",
		alias: "ID"
	}
];

const CSV_COLUMNS = [
	{
		id: "url",
		displayName: "searched URL"
	},
	{
		id: "result",
		displayName: "Result"
	},
	{
		id: "type",
		displayName: "Account Type"
	},
	{
		id: "name",
		displayName: "Name"
	},
	{
		id: "followers",
		displayName: "Followers"
	},
	{
		id: "crawling_status",
		displayName: "Crawling Status"
	},
	{
		id: "customers",
		displayName: "Customers"
	},
	{
		id: "profile_url",
		displayName: "Profile URL"
	},
	{
		id: "voice_url",
		displayName: "Voice URL"
	},
	{
		id: "voice_id",
		displayName: "Voice ID"
	}
];

const AUDIENCE_FIELD_TRANSLATION = {
	facebook: "fans",
	youtube: "subscribers"
};

function getFoundFragmentHeader(result) {
	let cardSubtitle;
	if (result.content.result.length > 0) {
		cardSubtitle = (
			<h6 className="term-total-result">
				Total results: {result.content.result.length}
			</h6>
		);
	}

	return (
		<>
			<Card.Title>
				Search term: {result.url}
			</Card.Title>
			{cardSubtitle}
		</>
	);
}

function getFoundFragment(result) {
	return (
		<AdvancedTable
			key={result.url}
			columns={TABLE_COLUMNS}
			data={processResponse(result)}
			hidePagination={true}
		>
			{getFoundFragmentHeader(result)}
		</AdvancedTable>
	);
}

function getNotFoundFragment(result) {
	return (
		<Card key={`term_search_${result.url}`}>
			<Card.Body>
				<Card.Title className={"term-not-found"}>Search term: {result.url}</Card.Title>
				<div className={"term-total-result"}>Total results: 0</div>
			</Card.Body>
		</Card>
	);
}

function processResponse(response) {

	const { content, url } = response;

	const getNameAndFollowers = (type, profile) => {
		return (
			<>
				<ProfileImage id={profile.id} type={type} className="img-fluid rounded-circle mb-2" width="40" height="40" />
				<span className="ml-2">{profile.name} - <NumeralMetric>{profile.audience}</NumeralMetric> {AUDIENCE_FIELD_TRANSLATION[type] ? AUDIENCE_FIELD_TRANSLATION[type] : "followers"}</span>
			</>
		);
	}

	const getAccountLink = (type, profile) => {
		const deepBlueProfileLink = `/${type}/profile?id=${profile.id}`;
		return (
			<Link children={profileTag(type, profile)} href={deepBlueProfileLink} target="_blank" className="custom-link"/>
		);
	}

	const getExternalAccountLink = (url) => {
		const externalProfileLink = url;
		return (
			<Link children={<ExternalLinkOutline height={23} width={19} className="ml-1" />} href={externalProfileLink} target="_blank"/>
		);
	}



	const getCrawlingStatus = (type, profile) => {
		const isOrphan = Boolean(!profile.voice);
		return (
			<>
				<CrawlingSwitch
					checked={profile.crawling.customers.length}
					isOrphan={isOrphan}
					voiceId={profile.voice ? profile.voice.id : null}
					profileId={profile.id}
					type={type} />
			</>
		)
	}

	const getCustomerBadges = (crawling) => {
		return crawling.customers.map(customer => {
			const name = customer.name.replace(/^[A-Z]+\s/, "");
			return <Badge key={customer.id} label={name} />
		});
	}

	const getVoiceIdLink = (profile) => {
		const voice_id = profile.voice?.id ?? null
		const deepBlueVoiceLink = `/voices/${voice_id}`;
		return (
			<Link children={voice_id} href={deepBlueVoiceLink} target="_blank" className="custom-link"/>
		);
	}

	const rows = content.result.map(profile => {
		return {
			id: profile.id,
			account: <>
				{getAccountLink(content.type, profile)}
				{getExternalAccountLink(url)}
			</>,
			account_type: <VoiceRelationsIcons type={content.type} size="30" variant="plain_colored" />,
			name_followers: getNameAndFollowers(content.type, profile),
			crawling: getCrawlingStatus(content.type, profile),
			customers: getCustomerBadges(profile.crawling),
			voice_id: getVoiceIdLink(profile)
		}
	});

	return { total: response.content.result.length, rows };
}

function countSummary(results) {
	return results.reduce((count, item) => {
		count.total++;
		if (item.content?.result.length > 0) {
			if (item.content?.result?.[0]?.voice === null) {
				count.found++;
			}
			if (item.content?.result?.[0]?.voice !== null) {
				count.voiceMatch++;
			}
		} else {
			count.notFound++;
		}

		return count;
	}, { total: 0, found: 0, voiceMatch:0, notFound: 0 });
}

async function fetchAndEnrichBatch(k2Api, batchUrls) {
	const response = await k2Api.post('socialmedia/msearch', { urls: batchUrls });

	return Promise.all(response.data.map(async (profile) => {
		if (!profile.content) {
			return { ...profile, content: { result: [] } };
		}
		const resultData = await Promise.all(profile.content.result.map(async (item) => {
			const crawlingResponse = await k2Api.get(`/socialmedia/${profile.content.type}/crawling/${item.id}`);
			return { ...item, crawling: crawlingResponse.data };
		}));
		return { ...profile, content: { ...profile.content, result: resultData } };
	}));
}

function MSearchResult({ results, loading, currentPage, totalPages, totalUrls, onPageChange, allUrls, batchSize }) {
	const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
	const notFoundRef = useRef();
	const foundTerms = [];
	const notFoundTerms = [];

	results.forEach(result => {
		if (result.content?.result.length > 0) {
			foundTerms.push(getFoundFragment(result));
		} else {
			notFoundTerms.push(getNotFoundFragment(result));
		}
	});

	const { found, voiceMatch, notFound } = countSummary(results);

	const retrieveResultsToCsv = async() => {
		setIsDownloadingCsv(true);

		const k2Api = new K2Api();
		const allResults = [];
		for (let i = 0; i < allUrls.length; i += batchSize) {
			const batchUrls = allUrls.slice(i, i + batchSize);
			const enriched = await fetchAndEnrichBatch(k2Api, batchUrls);
			allResults.push(...enriched);
		}

        const datas = allResults.flatMap(searchedTerm => {
			const url = searchedTerm.url;
			const hasResults = searchedTerm.content?.result?.length > 0;

			if (!hasResults) return [{ url: url, result: 'Not found', type: searchedTerm.content.type }];

			return searchedTerm.content.result.map(item => ({
				url: url,
				result: hasResults ? 'Found' : 'Not found',
				type: searchedTerm.content.type,
				name: item.name,
				followers: item.audience,
				crawling_status: item.crawling.customers.length ? 'Enabled' : 'Disabled',
				customers: item.crawling.customers.map(customer => customer.name).join('-'),
				profile_url: `${window.location.origin}/${searchedTerm.content.type}/profile?id=${item.id}`,
				voice_url: item.voice ? `${window.location.origin}/voices/${item.voice.id}` : '',
				voice_id: item.voice ? item.voice.id : ''
			}));
		});

		setIsDownloadingCsv(false);
		return datas;
    };

	const batchStart = (currentPage - 1) * batchSize + 1;
	const batchEnd = Math.min(currentPage * batchSize, totalUrls);

	return (
		<>
			<div className="voice-search-results">
				<h1>Orphan / Mass Search - Results</h1>
				<div className="d-flex flex-row justify-content-between mb-5">
					<h4 className="page-subtitle">
						<span>Terms searched: {totalUrls}</span>
						{totalPages > 1 && <span> | Showing {batchStart}-{batchEnd}</span>}
						{!loading && (
							<>
								<span> | Found: {found}</span>
								<span> | Voice match: {voiceMatch}</span>
								<span> | Not found: {notFound}</span>
							</>
						)}
					</h4>
					<ExportCSV columns={CSV_COLUMNS} disabled={isDownloadingCsv} datas={retrieveResultsToCsv} filename="OrphansMassSearchReport" buttonText="Download results in CSV" />
				</div>
				{loading ? (
					<div className="d-flex justify-content-center my-5">
						<Loader size={40} />
					</div>
				) : (
					<>
						<div>{foundTerms}</div>
						<div ref={notFoundRef} id="not_found_terms">{notFoundTerms}</div>
					</>
				)}
				{totalPages > 1 && (
					<div className="d-flex flex-row justify-content-end align-items-center mt-3 gap-3 msearch-pagination">
						{loading && <Loader size={40} />}
						<TablePagination
							onRowsPerPageChange={() => {}}
							onPageChange={onPageChange}
							count={totalUrls}
							rowsPerPage={batchSize}
							page={currentPage}
						/>
					</div>
				)}
			</div>
			<GoTopButton />
		</>
	);
}

export default MSearchResult;
